import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../components/ResultsPage.css';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scores, setScores] = useState(null);
  const [activeTab, setActiveTab] = useState('engagement');
  
  const scrollToCard = (cardId, tabName) => {
    setActiveTab(tabName);
    const element = document.getElementById(cardId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    // Get scores from location state or localStorage
    const locationScores = location.state?.scores;
    if (locationScores) {
      setScores(locationScores);
      // Also save to localStorage as backup
      localStorage.setItem('questionnaireScores', JSON.stringify(locationScores));
      console.log('Scores received:', locationScores);
    } else {
      // Try to get from localStorage
      const savedScores = localStorage.getItem('questionnaireScores');
      if (savedScores) {
        setScores(JSON.parse(savedScores));
      } else {
        // No scores found, redirect to home
        navigate('/');
      }
    }
  }, [location, navigate]);

  // Responsive chart sizes
  const [chartSizes, setChartSizes] = useState({ overall: 280, category: 200, stroke: 30 });

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w <= 480) {
        setChartSizes({ overall: 180, category: 140, stroke: 22 });
      } else if (w <= 768) {
        setChartSizes({ overall: 220, category: 170, stroke: 26 });
      } else {
        setChartSizes({ overall: 280, category: 200, stroke: 30 });
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Donut Chart Component
  const DonutChart = ({ percentage, label, size = 200, strokeWidth = 30, isOverall = false }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    
    // Color based on percentage
    let fillColor = '#EF4444'; // red for low
    let bgColor = '#FEE2E2'; // light red background
    
    if (percentage >= 67) {
      fillColor = '#10B981'; // green for high
      bgColor = '#D1FAE5'; // light green background
    } else if (percentage >= 34) {
      fillColor = '#F97316'; // orange for medium
      bgColor = '#FFEDD5'; // light orange background
    }
    
    // For overall chart, use orange for filled and red for background (as per design)
    if (isOverall) {
      fillColor = '#F97316'; // orange
      bgColor = '#FEE2E2'; // light red/pink
    }

    return (
      <div className="donut-chart-container">
        <svg width={size} height={size} className="donut-chart">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={bgColor}
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            className="donut-progress"
          />
        </svg>
        <div className="donut-chart-label">
          <div className="donut-chart-label-text">{label}</div>
          <div className="donut-chart-percentage" style={{ color: isOverall ? '#F97316' : fillColor }}>
            {percentage}%
          </div>
        </div>
      </div>
    );
  };

  if (!scores) {
    return <div>Loading...</div>;
  }

  const getLevelClass = (percent) => {
    if (percent >= 67) return 'high';
    if (percent >= 34) return 'medium';
    return 'low';
  };

  // Dynamic copy based on percentage and category
  const getCategoryCopy = (category, percent) => {
    const pct = Number.isFinite(percent) ? percent : 0;
    const isHigh = pct >= 67;
    const isMed = pct >= 34 && pct < 67;

    if (category === 'engagement') {
      if (isHigh) {
        return 'Great work, your customers are actively engaging with your brand and returning to order again. Keep momentum by continuing consistent promos, social content, and email nudges tied to your online menu.';
      }
      if (isMed) {
        return 'You have a solid base of engagement, but there is room to increase repeat orders. Consider simple loyalty rewards, upsell prompts in checkout, and regular menu spotlights to re‑engage customers.';
      }
      return 'Engagement is currently low which can limit online orders. Start with clear calls‑to‑action, simple first‑order incentives, and consistent posting that links directly to your online ordering pages.';
    }

    // ordering efficiency copy
    if (isHigh) {
      return 'Your ordering flow is efficient and easy to use. Maintain performance with periodic UX checks, fast page speed, and streamlined checkout to keep conversion rates high.';
    }
    if (isMed) {
      return 'Your ordering experience is workable but has friction. Reduce steps at checkout, surface popular items, and ensure payment/options are clear to increase completed orders.';
    }
    return 'Ordering efficiency is holding back conversions. Simplify navigation to the menu, clarify fees and delivery times early, and minimize required inputs to help customers complete orders quickly.';
  };

  const getNextSteps = (category, percent) => {
    const pct = Number.isFinite(percent) ? percent : 0;
    const isHigh = pct >= 67;
    const isMed = pct >= 34 && pct < 67;

    if (category === 'engagement') {
      if (isHigh) {
        return 'Keep acting on what is working: schedule regular menu teasers, trigger post-order thank-you emails with upsells, and monitor campaign analytics weekly so your loyal audience stays engaged.';
      }
      if (isMed) {
        return 'Launch a simple loyalty offer in the online ordering flow, send bi-weekly menu spotlights, and test limited-time bundles to encourage those semi-regular guests to order more often.';
      }
      return 'Start with awareness builders: promote a first-order incentive everywhere you show up digitally, collect emails during checkout, and follow up with an automated welcome sequence that links straight back to your menu.';
    }

    if (isHigh) {
      return 'Keep the experience frictionless by reviewing abandonment analytics regularly, refreshing hero dishes seasonally, and stress-testing checkout on mobile to stay conversion-ready.';
    }
    if (isMed) {
      return 'Audit every ordering step, streamline checkout fields, enable stored payment or guest checkout, and surface your top sellers to guide guests through to completion.';
    }
    return 'Rebuild the flow for clarity: simplify navigation categories, show delivery fees and timing up front, and pilot an ordering platform that syncs to your POS so staff are never rekeying orders.';
  };

  // Calculate overall score as average of the two category percentages
  const overallScore = Math.round(
    (scores.orderingEfficiencyPercent + scores.customerEngagementPercent) / 2
  ) || 35; // Default to 35 if scores not available

  // Get email from localStorage or use placeholder
  const userEmail = localStorage.getItem('userEmail') || 'your email';

  return (
    <>
      <SEO 
        title="Restaurant Online Ordering Assessment Results - View Your Score | Order Portal"
        description="View your restaurant's online ordering assessment results. Get your personalized score for Customer Engagement and Ordering Efficiency, plus actionable insights to increase your restaurant's online orders."
        keywords="restaurant assessment results, online ordering score, restaurant digital score, customer engagement score, ordering efficiency, restaurant improvement tips"
        canonical="https://orderportal.com/results"
      />
      <main className="results-page" role="main">
        <div className="results-container">
          {/* Left Section - Text Content */}
          <div className="results-left">
            <h1 className="results-title">
              Thank you for taking the <strong>assessment</strong>
            </h1>

          <div className="email-confirmation">
            <p className="email-text">
              Your results have been emailed to <span className="email-address">{userEmail}</span>.
            </p>
          </div>

          <h2 className="overall-score-heading">Here is your overall online impact score!</h2>

          <div className="results-description">
            <p>
            Today’s restaurant success starts online and the way your business shows up digitally is just as important as the food you serve.
            </p>
            <p>
            To thrive, you need a connected strategy that combines powerful online ordering, consistent branding, smart customer data, and marketing that turns online visitors into loyal repeat customers.
            </p>
            <p>
              We have broken this down into two key areas of focus: <strong>Customer Engagement & Ordering Efficiency</strong>.
            </p>
            <p>
              Below you will find your score for each one of these categories and tips on how to improve for next time.
            </p>
          </div>

          <div className="legend">
            <div className="legend-item">
              <span className="legend-circle legend-low"></span>
              <span className="legend-label">low</span>
            </div>
            <div className="legend-item">
              <span className="legend-circle legend-medium"></span>
              <span className="legend-label">medium</span>
            </div>
            <div className="legend-item">
              <span className="legend-circle legend-high"></span>
              <span className="legend-label">high</span>
            </div>
          </div>
        </div>

        {/* Right Section - Charts */}
        <div className="results-right">
          {/* Overall Score Chart */}
          <div className="chart-section">
            <DonutChart 
              percentage={overallScore} 
              label="Your Overall Score"
              size={chartSizes.overall}
              strokeWidth={chartSizes.stroke}
              isOverall={true}
            />
          </div>

          {/* Category Score Charts */}
          <div className="category-charts">
            <div className="category-chart-item">
              <h3 className="category-title">Customer Engagement</h3>
              <DonutChart 
                percentage={scores.customerEngagementPercent || 0} 
                label="Customer Engagement"
                size={chartSizes.category}
                strokeWidth={chartSizes.stroke}
              />
            </div>
            <div className="category-chart-item">
              <h3 className="category-title">Ordering Efficiency</h3>
              <DonutChart 
                percentage={scores.orderingEfficiencyPercent || 0} 
                label="Ordering Efficiency"
                size={chartSizes.category}
                strokeWidth={chartSizes.stroke}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Category Details Section */}
      <div className="category-details">
        <div className="category-details-inner">
          <h2 className="category-details-heading">We've scored you against the following key areas:</h2>
          <div className="category-tabs">
            <button 
              className={`category-tab ${activeTab === 'engagement' ? 'active' : ''}`}
              onClick={() => scrollToCard('engagement-card', 'engagement')}
            >
              Customer Engagement
            </button>
            <button 
              className={`category-tab ${activeTab === 'efficiency' ? 'active' : ''}`}
              onClick={() => scrollToCard('efficiency-card', 'efficiency')}
            >
              Ordering Efficiency
            </button>
          </div>

          <div id="engagement-card" className="category-card">
            <div className="category-card-header">
              <div className="category-card-title">
                <img src="/thumbsup.svg" alt="Engagement" className="category-card-icon" />
                <span className="category-card-name">Customer Engagement</span>
              </div>
              <div className={`category-pill ${getLevelClass(scores.customerEngagementPercent || 0)}`}>
                {scores.customerEngagementPercent || 0}%
              </div>
            </div>

            <div className="category-card-body">
              <p className="category-intro">
                Customer engagement is crucial for growing your online ordering business. Strong engagement means more people discovering your menu, interacting with your brand, and placing orders regularly.
              </p>
              <p className="category-copy">{getCategoryCopy('engagement', scores.customerEngagementPercent || 0)}</p>
              <p className="category-next-steps">
                <strong>Next Steps:</strong> {getNextSteps('engagement', scores.customerEngagementPercent || 0)}
              </p>
            </div>
          </div>

          <div id="efficiency-card" className="category-card category-card-spaced">
            <div className="category-card-header">
              <div className="category-card-title">
                <img src="/influence.svg" alt="Ordering Efficiency" className="category-card-icon" />
                <span className="category-card-name">Ordering Efficiency</span>
              </div>
              <div className={`category-pill ${getLevelClass(scores.orderingEfficiencyPercent || 0)}`}>
                {scores.orderingEfficiencyPercent || 0}%
              </div>
            </div>

            <div className="category-card-body">
              <p className="category-intro">
                Ordering efficiency is crucial for customer satisfaction and business growth. Streamlined ordering processes reduce friction and make it easy for customers to place repeat orders.
              </p>
              <p className="category-copy">{getCategoryCopy('efficiency', scores.orderingEfficiencyPercent || 0)}</p>
              <p className="category-next-steps">
                <strong>Next Steps:</strong> {getNextSteps('efficiency', scores.orderingEfficiencyPercent || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-image-wrap">
            <img src="/onlineordering.svg" alt="Maximize your restaurant's online ordering potential with Order Portal" className="cta-image" />
          </div>
          <div className="cta-content">
            <h2 className="cta-title">Would you like to Maximize your Online Ordering!</h2>
            <p className="cta-paragraph">
              If you'd like to delve further into your results, we would be delighted to share our insight and ideas
              with you. Please click below to schedule a no obligation conversation. We will be armed with the
              information you have provided in this scorecard and be ready to help you not only increase your score
              but get the most value from your online ordering to grow your business.
            </p>
            <a href="https://calendly.com/enterprisewebsolutions22/30min" target="_blank" rel="noreferrer" className="cta-button">Book a Free Discovery Session</a>
          </div>
        </div>
      </section>

      <Footer />
      </main>
    </>
  );
};

export default ResultsPage;

